// @ts-strict-ignore
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Form from 'antd/lib/form';
import Space from 'antd/lib/space';
import { compact, flatMap } from 'lodash';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateFeatureItemsFilters, updateShowFixesOnly } from '../../actions/filters';
import { RootState } from '../../reducers/reducers';
import {
    selectFiltersMap,
    selectTicketOwners,
    selectCodeAuthors,
    selectCodeFAs,
    selectTicketProjects,
    selectTicketStatuses,
    selectTicketFAs,
    selectAssociatedIssues,
    selectFirstSeenInStagingBuildValues,
    selectIssuesByBuild,
    selectBuildsIssueStore,
    selectTicketPriorities,
    selectTicketTypes,
    selectTicketCustomFieldValues,
} from '../../selectors/memoizedSelectors';
import {
    AnyFilter,
    AssociatedIssuesFilter,
    CodeAuthorFilter,
    CodeFAFilter,
    CodeStatusFilter,
    FilterType,
    FilterTypeOrCustomField,
    FirstSeenInRCBuildFilter,
    HideSubtasksFilter,
    HideUnsquashedChildCommitsFilter,
    OnlyShowItemsWithFAsFilter,
    OnlyShowFixesOnly,
    TicketCustomFieldFilter,
    TicketFAFilter,
    TicketOwnerFilter,
    TicketPriorityFilter,
    TicketProjectFilter,
    TicketStatusFilter,
    TicketTypeFilter,
    OnlyShowItemsWithoutTickets,
} from '../../types/filters';
import { antGrey, tagStyle } from '../styles/globalStyles';
import Select from 'antd/lib/select';
import { AppStoreAvailableBuild, FeatureItemCodeStatus, LifecycleStep } from '../../types';
import { Nullable, allCases } from '../../typescriptHelpers';
import { displayCodeStatusForStatus } from '../../helpers/displayHelpers';
import { getBuildsWithStabilityInfo, tagColorForFeatureItemCodeStatus } from '../../helpers';
import Tag from 'antd/lib/tag';
import { CustomTagProps } from 'rc-select/lib/BaseSelect';
import Text from 'antd/lib/typography/Text';
import Cascader from 'antd/lib/cascader';
import Divider from 'antd/lib/divider';
import { useLifecycleStepCompleted } from '../automations/automationHelperComponents';
import { selectFeatureWorkItemsCustomTicketFields } from '../../selectors/selectors';
import { filterOptionValue } from '../../helpers/formHelpers';

interface FiltersComponentProps {
    useWorkingBranch: boolean;
    releaseId: string;
    stepRunId: string;
    appId: string;
    isVisible: boolean;
    onApplyFilters: (filters: AnyFilter[]) => void;
    appStoreSelectedBuilds: Nullable<AppStoreAvailableBuild[]>;
    isAutoMergeRunwayPRsEnabled: boolean;
}

const MY_VCS_USERNAME_TICKET_OWNER = '#MY_VCS_USERNAME#';

const ticketCustomFieldKey = (customFieldName: string): `${FilterType.TicketCustomField}${string}` => {
    return `${FilterType.TicketCustomField}${customFieldName}`;
};

const FiltersComponent: React.FC<FiltersComponentProps> = (props) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const currentFiltersMap = useSelector((state: RootState) => selectFiltersMap(state));
    const allPossibleTicketOwners = useSelector((state: RootState) => selectTicketOwners(state, props.stepRunId));
    const allPossibleCodeOwners = useSelector((state: RootState) => selectCodeAuthors(state, props.stepRunId));
    const allPossibleCodeFAs = useSelector((state: RootState) => selectCodeFAs(state, props.stepRunId));
    const allPossibleTicketProjects = useSelector((state: RootState) => selectTicketProjects(state, props.stepRunId));
    const allPossibleTicketStatuses = useSelector((state: RootState) => selectTicketStatuses(state, props.stepRunId));
    const allPossibleTicketFAs = useSelector((state: RootState) => selectTicketFAs(state, props.stepRunId));
    const allPossibleTicketPriorities = useSelector((state: RootState) =>
        selectTicketPriorities(state, props.stepRunId),
    );
    const allPossibleTicketTypes = useSelector((state: RootState) => selectTicketTypes(state, props.stepRunId));
    const allPossibleAssociatedIssues = useSelector((state: RootState) =>
        selectAssociatedIssues(state, props.stepRunId),
    );
    const allPossibleFirstSeenInStagingBuildValues = useSelector((state: RootState) =>
        selectFirstSeenInStagingBuildValues(state, props.stepRunId),
    );

    const appStoreSelectedBuilds = props.appStoreSelectedBuilds;
    const buildsWithStabilityInfo = getBuildsWithStabilityInfo(appStoreSelectedBuilds);
    const isComplete = useLifecycleStepCompleted(LifecycleStep.Release, props.releaseId);
    const hasMultipleBuilds = isComplete && buildsWithStabilityInfo?.length > 1;

    const buildIssuesStore = useSelector((state: RootState) => selectBuildsIssueStore(state, props.stepRunId));
    const multiBuildIssueOptions = useSelector((state: RootState) => selectIssuesByBuild(state, props.stepRunId));

    const ticketCustomFields = useSelector((state: RootState) =>
        selectFeatureWorkItemsCustomTicketFields(state, props.stepRunId),
    );

    const allPossibleValuesForCustomFields = useSelector((state: RootState) =>
        selectTicketCustomFieldValues(state, props.stepRunId),
    );

    const applyFilters = (values: Record<FilterType, any>) => {
        const filters: AnyFilter[] = compact(
            flatMap(values, (value, type) => {
                switch (type) {
                    case FilterType.HideSubtasks: {
                        if (values.hideSubtaskTickets) {
                            return {
                                type,
                                hideSubtasks: value,
                            };
                        }
                        return null;
                    }
                    case FilterType.HideUnsquashedChildCommits: {
                        if (values.hideUnsquashedChildCommits) {
                            return {
                                type,
                                hideUnsquashedChildCommits: value,
                            };
                        }
                        return null;
                    }
                    case FilterType.HideItemsWithMissingFAs: {
                        if (values.hideMissingFeatureAffiliations) {
                            return {
                                type,
                                hideMissingFeatureAffiliations: value,
                            };
                        }
                        return null;
                    }
                    case FilterType.HideItemsWithTickets: {
                        if (values.hideItemsWithTickets) {
                            return {
                                type,
                                hideItemsWithTickets: value,
                            };
                        }
                        return null;
                    }
                    case FilterType.ShowFixesOnly: {
                        if (values.showFixesOnly) {
                            dispatch(updateShowFixesOnly({ showFixesOnly: true, appId: props.appId }));
                            return {
                                type,
                                showFixesOnly: value,
                            };
                        }
                        dispatch(updateShowFixesOnly({ showFixesOnly: false, appId: props.appId }));
                        return null;
                    }
                    case FilterType.TicketOwner: {
                        if (values.ticketOwner) {
                            return values.ticketOwner.map((ticketOwner) => {
                                return {
                                    type,
                                    ticketOwner,
                                };
                            });
                        }

                        return null;
                    }
                    case FilterType.TicketFA: {
                        if (values.ticketFA) {
                            return values.ticketFA.map((ticketFA) => {
                                return {
                                    type,
                                    ticketFA,
                                };
                            });
                        }

                        return null;
                    }
                    case FilterType.CodeFA: {
                        if (values.codeFA) {
                            return values.codeFA.map((codeFA) => {
                                return {
                                    type,
                                    codeFA,
                                };
                            });
                        }

                        return null;
                    }
                    case FilterType.CodeAuthor: {
                        if (values.codeAuthor) {
                            return values.codeAuthor.map((codeAuthor) => {
                                return {
                                    type,
                                    codeAuthor,
                                };
                            });
                        }

                        return null;
                    }
                    case FilterType.CodeStatus: {
                        if (values.codeStatus) {
                            return values.codeStatus.map((status) => {
                                return {
                                    type,
                                    codeStatus: status,
                                };
                            });
                        }

                        return null;
                    }
                    case FilterType.TicketProject: {
                        if (values.ticketProject) {
                            const selectedProjects = allPossibleTicketProjects.filter((project) =>
                                values.ticketProject.includes(project.identifier),
                            );

                            return selectedProjects.map((project) => {
                                return {
                                    type,
                                    ticketProjectId: project.identifier,
                                    ticketProjectDisplayName: project.displayableName,
                                };
                            });
                        }

                        return null;
                    }
                    case FilterType.TicketStatus: {
                        if (values.ticketStatus) {
                            return values.ticketStatus.map((status) => {
                                return {
                                    type,
                                    status,
                                };
                            });
                        }

                        return null;
                    }
                    case FilterType.TicketPriority: {
                        if (values.ticketPriority) {
                            return values.ticketPriority.map((priority) => {
                                return {
                                    type,
                                    priority,
                                };
                            });
                        }

                        return null;
                    }
                    case FilterType.TicketType: {
                        if (values.ticketType) {
                            return values.ticketType.map((ticketType) => {
                                return {
                                    type,
                                    ticketType,
                                };
                            });
                        }

                        return null;
                    }
                    case FilterType.FirstSeenInRCBuild: {
                        if (values.firstSeenInRCBuild?.length > 0) {
                            return values.firstSeenInRCBuild.map((buildIdentifier: string) => ({
                                type,
                                buildIdentifier,
                            }));
                        }
                        return null;
                    }
                    case FilterType.AssociatedIssues: {
                        if (values.associatedIssues) {
                            if (Array.isArray(values.associatedIssues[0])) {
                                //if inner array has more than one value, filter by associated issues, issues.slice(1)
                                const valueIssues = [];
                                let isFilteredByBuild;
                                values.associatedIssues.forEach((group) => {
                                    if (group.length > 1) {
                                        const issues = group.slice(1);
                                        issues.forEach((issue) => {
                                            valueIssues.push(issue);
                                        });
                                        isFilteredByBuild = false;
                                    } else {
                                        //get all issues in build, add to valueIssues
                                        const issues = buildIssuesStore[group[0]];
                                        issues.forEach((issue) => {
                                            valueIssues.push(issue.issueID);
                                        });
                                        isFilteredByBuild = true;
                                    }
                                });

                                const selectedIssues = allPossibleAssociatedIssues.filter((allIssue) =>
                                    valueIssues.includes(allIssue.stabilityIssue.issueID),
                                );
                                return selectedIssues.map((issue) => {
                                    return {
                                        type,
                                        associatedIssueId: issue.stabilityIssue.issueID,
                                        associatedIssueShortDescription: issue.stabilityIssue.crashShortDescription,
                                        buildId: issue.buildIdentifier,
                                        isFilteredByBuild,
                                    };
                                });
                            } else if (Array.isArray(values.associatedIssues)) {
                                //if array of strings, filter by issues
                                const selectedIssues = allPossibleAssociatedIssues.filter((issue) =>
                                    values.associatedIssues.includes(issue.stabilityIssue.issueID),
                                );

                                return selectedIssues.map((issue) => {
                                    return {
                                        type,
                                        associatedIssueId: issue.stabilityIssue.issueID,
                                        associatedIssueShortDescription: issue.stabilityIssue.crashShortDescription,
                                        buildId: issue.buildIdentifier,
                                        isFilteredByBuild: false,
                                    };
                                });
                            }
                        }
                        return null;
                    }
                    default:
                        // special handling for custom field filters
                        if (type.startsWith(FilterType.TicketCustomField)) {
                            return value.map((customFieldValue) => {
                                return {
                                    type: FilterType.TicketCustomField,
                                    key: type,
                                    value: customFieldValue,
                                };
                            });
                        }
                        // unsupported filters
                        return null;
                }
            }),
        );

        dispatch(updateFeatureItemsFilters({ filters, appId: props.appId }));

        props.onApplyFilters(filters);
    };

    const clearButtonClicked = () => {
        dispatch(updateFeatureItemsFilters({ filters: [], appId: props.appId }));
        form.resetFields();
    };

    React.useEffect(() => {
        if (Object.keys(currentFiltersMap).length === 0) {
            form.resetFields();
        }
    }, [currentFiltersMap]);

    const initialValues: Record<FilterTypeOrCustomField, unknown> = {
        hideSubtaskTickets:
            currentFiltersMap[FilterType.HideSubtasks] ?
                (currentFiltersMap[FilterType.HideSubtasks] as HideSubtasksFilter[])[0].hideSubtasks
            :   null,
        hideUnsquashedChildCommits:
            currentFiltersMap[FilterType.HideUnsquashedChildCommits] ?
                (currentFiltersMap[FilterType.HideUnsquashedChildCommits] as HideUnsquashedChildCommitsFilter[])[0]
                    .hideUnsquashedChildCommits
            :   null,
        hideMissingFeatureAffiliations:
            currentFiltersMap[FilterType.HideItemsWithMissingFAs] ?
                (currentFiltersMap[FilterType.HideItemsWithMissingFAs] as OnlyShowItemsWithFAsFilter[])[0]
                    .hideMissingFeatureAffiliations
            :   null,
        hideItemsWithTickets:
            currentFiltersMap[FilterType.HideItemsWithTickets] ?
                (currentFiltersMap[FilterType.HideItemsWithTickets] as OnlyShowItemsWithoutTickets[])[0]
                    .hideItemsWithTickets
            :   null,
        showFixesOnly:
            currentFiltersMap[FilterType.ShowFixesOnly] ?
                (currentFiltersMap[FilterType.ShowFixesOnly] as OnlyShowFixesOnly[])[0].showFixesOnly
            :   null,
        ticketOwner:
            currentFiltersMap[FilterType.TicketOwner] ?
                (currentFiltersMap[FilterType.TicketOwner] as TicketOwnerFilter[]).map((filter) => filter.ticketOwner)
            :   [],
        ticketStatus:
            currentFiltersMap[FilterType.TicketStatus] ?
                (currentFiltersMap[FilterType.TicketStatus] as TicketStatusFilter[]).map((filter) => filter.status)
            :   [],
        ticketFA:
            currentFiltersMap[FilterType.TicketFA] ?
                (currentFiltersMap[FilterType.TicketFA] as TicketFAFilter[]).map((filter) => filter.ticketFA)
            :   [],
        codeAuthor:
            currentFiltersMap[FilterType.CodeAuthor] ?
                (currentFiltersMap[FilterType.CodeAuthor] as CodeAuthorFilter[]).map((filter) => filter.codeAuthor)
            :   [],
        codeStatus:
            currentFiltersMap[FilterType.CodeStatus] ?
                (currentFiltersMap[FilterType.CodeStatus] as CodeStatusFilter[]).map((filter) => filter.codeStatus)
            :   [],
        codeFA:
            currentFiltersMap[FilterType.CodeFA] ?
                (currentFiltersMap[FilterType.CodeFA] as CodeFAFilter[]).map((filter) => filter.codeFA)
            :   [],
        ticketProject:
            currentFiltersMap[FilterType.TicketProject] ?
                (currentFiltersMap[FilterType.TicketProject] as TicketProjectFilter[]).map(
                    (filter) => filter.ticketProjectId,
                )
            :   [],
        ticketPriority:
            currentFiltersMap[FilterType.TicketPriority]?.map((filter) => (filter as TicketPriorityFilter).priority) ??
            [],
        firstSeenInRCBuild:
            currentFiltersMap[FilterType.FirstSeenInRCBuild] ?
                (currentFiltersMap[FilterType.FirstSeenInRCBuild] as FirstSeenInRCBuildFilter[]).map(
                    (filter) => filter.buildIdentifier,
                )
            :   [],
        associatedIssues:
            currentFiltersMap[FilterType.AssociatedIssues] ?
                (currentFiltersMap[FilterType.AssociatedIssues] as AssociatedIssuesFilter[]).map((filter) => {
                    if (hasMultipleBuilds) {
                        if (filter.isFilteredByBuild) {
                            return [filter.buildId];
                        } else {
                            return [filter.buildId, filter.associatedIssueId];
                        }
                    } else {
                        return filter.associatedIssueId;
                    }
                })
            :   [],
        ticketType: currentFiltersMap[FilterType.TicketType]?.map((filter) => (filter as TicketTypeFilter).ticketType),
        ...ticketCustomFields.reduce(
            (acc, customField) => ({
                ...acc,
                [ticketCustomFieldKey(customField)]:
                    currentFiltersMap[FilterType.TicketCustomField] ?
                        (currentFiltersMap[FilterType.TicketCustomField] as TicketCustomFieldFilter[])
                            .filter((filter) => ticketCustomFieldKey(customField) == filter.key)
                            .map((filter) => filter.value)
                    :   [],
            }),
            {},
        ),
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <div
                style={{
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingTop: 18,
                    paddingBottom: 18,
                    maxHeight: 450,
                    overflowY: 'scroll',
                }}
            >
                <Form
                    form={form}
                    initialValues={initialValues}
                    onFinish={applyFilters}
                    preserve={false}
                    layout="vertical"
                    className="filters-form"
                >
                    <Space direction="vertical" size={12}>
                        <Form.Item name="hideSubtaskTickets" valuePropName="checked" noStyle>
                            <Checkbox>Hide tickets that are subtasks</Checkbox>
                        </Form.Item>
                        <Form.Item name="hideMissingFeatureAffiliations" valuePropName="checked" noStyle>
                            <Checkbox>Only show tickets with the feature affiliation for this release</Checkbox>
                        </Form.Item>
                        <Form.Item name="hideItemsWithTickets" valuePropName="checked" noStyle>
                            <Checkbox>Only show items that are missing tickets</Checkbox>
                        </Form.Item>
                        <Form.Item name="hideUnsquashedChildCommits" valuePropName="checked" noStyle>
                            <Checkbox>Hide child commits from unsquashed PRs</Checkbox>
                        </Form.Item>
                        <Form.Item name="showFixesOnly" valuePropName="checked" noStyle>
                            <Checkbox>Show fixes only</Checkbox>
                        </Form.Item>

                        <Form.Item name="ticketOwner" label="Ticket owners">
                            <Select
                                allowClear
                                showSearch
                                mode="multiple"
                                placeholder="Select ticket owners"
                                filterOption={filterOptionValue}
                                maxTagCount={3}
                                style={{ maxWidth: 400 }}
                            >
                                {allPossibleTicketOwners.map((owner, index) => (
                                    <Select.Option value={owner} key={index}>
                                        {owner}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="ticketFA" label="Ticket feature affiliations">
                            <Select
                                allowClear
                                showSearch
                                mode="multiple"
                                placeholder="Select feature affiliations"
                                filterOption={filterOptionValue}
                                maxTagCount={3}
                                style={{ maxWidth: 400 }}
                            >
                                {allPossibleTicketFAs.map((fa, index) => (
                                    <Select.Option value={fa} key={index}>
                                        {fa}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="ticketProject" label="Ticket project" style={{ flexWrap: 'nowrap' }}>
                            <Select
                                allowClear
                                mode="multiple"
                                placeholder="Select ticket project"
                                style={{ maxWidth: 400 }}
                            >
                                {allPossibleTicketProjects.map((project) => (
                                    <Select.Option value={project.identifier} key={project.identifier}>
                                        {project.displayableName}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="ticketStatus" label="Ticket status">
                            <Select
                                allowClear
                                mode="multiple"
                                placeholder="Select ticket status"
                                style={{ maxWidth: 400 }}
                            >
                                {allPossibleTicketStatuses.map((status, index) => (
                                    <Select.Option value={status} key={index}>
                                        {status}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="ticketPriority" label="Ticket priority">
                            <Select
                                allowClear
                                mode="multiple"
                                placeholder="Select ticket priority"
                                style={{ maxWidth: 400 }}
                            >
                                {allPossibleTicketPriorities.map((priority, index) => (
                                    <Select.Option value={priority} key={index}>
                                        {priority}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="ticketType" label="Ticket type">
                            <Select
                                allowClear
                                mode="multiple"
                                placeholder="Select ticket type"
                                style={{ maxWidth: 400 }}
                            >
                                {allPossibleTicketTypes.map((ticketType, index) => (
                                    <Select.Option value={ticketType} key={index}>
                                        {ticketType}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        {ticketCustomFields.map((customField) => (
                            <Form.Item
                                name={ticketCustomFieldKey(customField)}
                                label={customField}
                                key={ticketCustomFieldKey(customField)}
                            >
                                <Select allowClear mode="multiple" placeholder="Select" style={{ maxWidth: 400 }}>
                                    {allPossibleValuesForCustomFields[customField] ?
                                        allPossibleValuesForCustomFields[customField].map((value, index) => (
                                            <Select.Option value={value} key={index}>
                                                {value}
                                            </Select.Option>
                                        ))
                                    :   []}
                                </Select>
                            </Form.Item>
                        ))}

                        <Divider type="horizontal" style={{ marginBottom: 0, marginTop: 15 }} />
                        <Form.Item name="codeAuthor" label="Commit or PR author">
                            <Select
                                allowClear
                                showSearch
                                mode="multiple"
                                placeholder="Select code authors"
                                filterOption={filterOptionValue}
                                maxTagCount={3}
                                style={{ maxWidth: 400 }}
                            >
                                <Select.Option value={MY_VCS_USERNAME_TICKET_OWNER} key={MY_VCS_USERNAME_TICKET_OWNER}>
                                    <strong>My user</strong>
                                </Select.Option>
                                {allPossibleCodeOwners.map((codeOwner, index) => (
                                    <Select.Option value={codeOwner} key={index}>
                                        {codeOwner}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="codeFA" label="PR label/milestone">
                            <Select
                                allowClear
                                showSearch
                                mode="multiple"
                                placeholder="Select feature affiliations"
                                filterOption={filterOptionValue}
                                maxTagCount={3}
                                style={{ maxWidth: 400 }}
                            >
                                {allPossibleCodeFAs.map((fa, index) => (
                                    <Select.Option value={fa} key={index}>
                                        {fa}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="codeStatus" label="Code status">
                            <Select
                                allowClear
                                mode="multiple"
                                tagRender={(customProps: CustomTagProps) => {
                                    return (
                                        <Tag
                                            color={tagColorForFeatureItemCodeStatus(
                                                props.isAutoMergeRunwayPRsEnabled,
                                                customProps.value as FeatureItemCodeStatus,
                                            )}
                                            style={{ ...tagStyle, marginRight: 3 }}
                                            closable={customProps.closable}
                                            onClose={customProps.onClose}
                                        >
                                            {customProps.label}
                                        </Tag>
                                    );
                                }}
                                placeholder="Select code status"
                                style={{ maxWidth: 400 }}
                            >
                                {allCases(FeatureItemCodeStatus).map((status, index) => (
                                    <Select.Option value={status} key={index}>
                                        {displayCodeStatusForStatus(status, props.useWorkingBranch)}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Divider type="horizontal" style={{ marginBottom: 0, marginTop: 10 }} />
                        {hasMultipleBuilds ?
                            <Form.Item name="associatedIssues" label="Associated issues by build">
                                <Cascader
                                    style={{ width: '100%' }}
                                    options={multiBuildIssueOptions}
                                    multiple
                                    maxTagCount="responsive"
                                ></Cascader>
                            </Form.Item>
                        :   <Form.Item name="associatedIssues" label="Associated issues">
                                <Select
                                    allowClear
                                    mode="multiple"
                                    placeholder="Select associated issues"
                                    style={{ maxWidth: 400 }}
                                    optionLabelProp="label"
                                >
                                    {allPossibleAssociatedIssues.map((issue) => (
                                        <Select.Option
                                            value={issue.stabilityIssue.issueID}
                                            key={issue.stabilityIssue.issueID}
                                            label={issue.stabilityIssue.crashShortDescription}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <Text>{issue.stabilityIssue.crashShortDescription}</Text>
                                                <Text ellipsis style={{ fontSize: 12, color: antGrey.grey7 }}>
                                                    {issue.stabilityIssue.crashLongDescription}
                                                </Text>
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        }
                        <Divider type="horizontal" style={{ marginBottom: 0, marginTop: 10 }} />
                        <Form.Item name="firstSeenInRCBuild" label="Item is new in RC build">
                            <Select allowClear mode="multiple" placeholder="Select build(s)" style={{ maxWidth: 400 }}>
                                {allPossibleFirstSeenInStagingBuildValues.map((buildId) => (
                                    <Select.Option value={buildId} key={buildId}>
                                        #{buildId}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Space>
                </Form>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: 12,
                    backgroundColor: antGrey.grey3,
                }}
            >
                <Button size="small" onClick={clearButtonClicked}>
                    Clear
                </Button>
                <Button size="small" onClick={form.submit} type="primary">
                    Apply
                </Button>
            </div>
        </div>
    );
};

export default FiltersComponent;
